// src/components/customer/PetBanner.tsx

import React, { useEffect, useRef, useState } from "react";
import CommonButton from "../CommonButton";
import { useAnalytics } from "../../utils/Customer/useAnalytics";

interface PetBannerProps {
  bannerId?: number;

  bannerPayload?: {
    title?: string;
    subTitle?: string;
    imageUrl: string;
    mobileImageUrl?: string;
  };

  handleShopNow?: () => void;
}

const PetBanner: React.FC<PetBannerProps> = ({
  bannerId,
  bannerPayload,
  handleShopNow,
}) => {
  const { trackBannerView, trackBannerClick } = useAnalytics();

  const bannerRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  // ------------------------------------------------------------
  // Banner content
  // ------------------------------------------------------------

  const headline = bannerPayload?.title || "Premium Pet Care Collection";

  const subHeadline =
    bannerPayload?.subTitle || "Up to 40% Off on Dog & Cat Essentials";

  const webImage =
    bannerPayload?.imageUrl ||
    "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=1200";

  const responsiveMobileImage = bannerPayload?.mobileImageUrl || webImage;

  // ------------------------------------------------------------
  // Banner visibility
  // ------------------------------------------------------------

  useEffect(() => {
    const element = bannerRef.current;

    if (!element || !bannerId) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.5,
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [bannerId]);

  // ------------------------------------------------------------
  // Banner View
  // ------------------------------------------------------------

  useEffect(() => {
    if (!isVisible || !bannerId) {
      return;
    }

    trackBannerView(bannerId);
  }, [isVisible, bannerId, trackBannerView]);

  // ------------------------------------------------------------
  // Banner Click
  // ------------------------------------------------------------

  const handleBannerClick = () => {
    if (bannerId) {
      trackBannerClick(bannerId);
    }

    handleShopNow?.();
  };

  return (
    <div ref={bannerRef} className="w-full">
      <div
        className="
          min-h-[16rem]
          sm:h-64
          md:h-72
          rounded-[2rem]
          border-2
          border-accent/20
          flex
          flex-col
          md:flex-row
          overflow-hidden
          relative
          shadow-sm
          hover:shadow-md
          transition
          duration-300
        "
      >
        {/* -------------------------------------------------
            TEXT BOX
        -------------------------------------------------- */}

        <div
          className="
            w-full
            md:w-[20%]
            bg-[#d9edd3]/90
            backdrop-blur-sm
            flex
            items-center
            p-6
            sm:p-8
            text-logoHeading
            z-10
            shrink-0
          "
        >
          <div className="space-y-3 w-full text-left">
            <h2
              className="
                text-xl
                sm:text-2xl
                font-black
                leading-tight
                tracking-tight
                whitespace-pre-line
              "
            >
              {headline}
            </h2>

            <p
              className="
                text-gray-700
                text-xs
                sm:text-sm
                font-medium
              "
            >
              {subHeadline}
            </p>

            <div className="pt-2">
              <CommonButton
                text="Shop Collection"
                variant="filled"
                rounded="md"
                className="
                  !bg-logoHeading
                  font-black
                  text-white
                  hover:!bg-black
                  border-none
                  shadow-xs
                  text-xs
                  px-6
                  py-2.5
                  transition
                  w-full
                "
                onClick={handleBannerClick}
              />
            </div>
          </div>
        </div>

        {/* -------------------------------------------------
            BANNER IMAGE
        -------------------------------------------------- */}

        <div
          className="
            w-full
            md:w-[80%]
            h-48
            md:h-full
            relative
            shrink-0
          "
        >
          <picture>
            <source media="(max-width: 640px)" srcSet={responsiveMobileImage} />

            <img
              src={webImage}
              alt="Campaign Promo Context"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </picture>
        </div>
      </div>
    </div>
  );
};

export default PetBanner;
